import { format } from 'date-fns';
import type { CardDateEntry, CardDateType, CardEvent, CardEventType } from '@creative-kit/shared';
import { CARD_DATE_TYPE_LABELS } from '@creative-kit/shared';

type FallbackHistoryEntry = {
  id: string;
};

type LiveHistoryEvent = Pick<CardEvent, 'type'>;

export function getMergedFallbackHistoryEntries<T extends FallbackHistoryEntry>(
  fallbackEntries: T[],
  liveEvents: LiveHistoryEvent[],
): T[] {
  const hasLiveCreated = liveEvents.some((evt) => evt.type === 'card.created');

  return hasLiveCreated
    ? fallbackEntries.filter((entry) => entry.id !== 'created-from' && entry.id !== 'created')
    : fallbackEntries;
}

export function getCardEventsStateAfterListenerError() {
  return {
    eventsLoaded: false,
    events: [] as CardEvent[],
  };
}

type BatchDatesChangedEventInput = {
  action: 'added' | 'updated' | 'removed' | 'set';
  dateType?: CardDateType;
  dateLabel?: string;
  dateValue?: string;
  userId: string;
  userEmail: string | null;
};

type BuiltCardEvent = {
  cardId: string;
  type: CardEventType;
  userId: string;
  userEmail: string | null;
  meta: Record<string, unknown>;
};

export function buildBatchDatesChangedEvents(
  cardIds: string[],
  input: BatchDatesChangedEventInput,
): BuiltCardEvent[] {
  const meta = {
    action: input.action,
    ...(input.dateType ? { dateType: input.dateType } : {}),
    ...(input.dateLabel ? { dateLabel: input.dateLabel } : {}),
    ...(input.dateValue ? { dateValue: input.dateValue } : {}),
  };

  return cardIds.map((cardId) => ({
    cardId,
    type: 'dates.changed',
    userId: input.userId,
    userEmail: input.userEmail,
    meta,
  }));
}

function getEntryKey(entry: CardDateEntry): string {
  return entry.type === 'custom' ? entry.id : entry.type;
}

function getDateLabelFromMeta(meta: Record<string, unknown>): string {
  const dateLabel = meta.dateLabel as string | undefined;
  if (dateLabel) return dateLabel;

  const dateType = meta.dateType as CardDateType | undefined;
  if (!dateType) return 'Date';
  return CARD_DATE_TYPE_LABELS[dateType] ?? 'Date';
}

export function formatCardDateValue(entry: CardDateEntry): string {
  const formatFull = (date: Date, hasTime: boolean) =>
    hasTime ? format(date, 'MMM d, h:mm a') : format(date, 'MMM d');

  if (entry.startDate && entry.endDate) {
    return `${formatFull(entry.startDate, entry.startHasTime)}-${formatFull(
      entry.endDate,
      entry.endHasTime,
    )}`;
  }
  if (entry.startDate) {
    return formatFull(entry.startDate, entry.startHasTime);
  }
  if (entry.endDate) {
    return formatFull(entry.endDate, entry.endHasTime);
  }
  return 'No date';
}

export function buildDatesChangedMeta({
  action,
  entry,
}: {
  action: 'added' | 'updated' | 'removed';
  entry: CardDateEntry;
}) {
  return {
    action,
    dateType: entry.type,
    dateLabel:
      entry.type === 'custom'
        ? entry.customLabel || CARD_DATE_TYPE_LABELS.custom
        : CARD_DATE_TYPE_LABELS[entry.type],
    dateValue: formatCardDateValue(entry),
  };
}

export function buildDateChangeEventsForCard({
  cardId,
  previousEntries,
  nextEntries,
  userId,
  userEmail,
}: {
  cardId: string;
  previousEntries: CardDateEntry[];
  nextEntries: CardDateEntry[];
  userId: string;
  userEmail: string | null;
}): BuiltCardEvent[] {
  const previousByKey = new Map(previousEntries.map((entry) => [getEntryKey(entry), entry]));
  const nextByKey = new Map(nextEntries.map((entry) => [getEntryKey(entry), entry]));
  const events: BuiltCardEvent[] = [];

  nextEntries.forEach((entry) => {
    const key = getEntryKey(entry);
    const previousEntry = previousByKey.get(key);
    if (!previousEntry) {
      events.push({
        cardId,
        type: 'dates.changed',
        userId,
        userEmail,
        meta: buildDatesChangedMeta({ action: 'added', entry }),
      });
      return;
    }

    if (JSON.stringify(previousEntry) !== JSON.stringify(entry)) {
      events.push({
        cardId,
        type: 'dates.changed',
        userId,
        userEmail,
        meta: buildDatesChangedMeta({ action: 'updated', entry }),
      });
    }
  });

  previousEntries.forEach((entry) => {
    const key = getEntryKey(entry);
    if (!nextByKey.has(key)) {
      events.push({
        cardId,
        type: 'dates.changed',
        userId,
        userEmail,
        meta: buildDatesChangedMeta({ action: 'removed', entry }),
      });
    }
  });

  return events;
}

function getHistoryStepLabel(
  stepId: string | null | undefined,
  session?: { sortedSteps?: Array<{ id: string; name?: string }> } | null,
): string {
  if (stepId == null) return 'Pending';
  return session?.sortedSteps?.find((step) => step.id === stepId)?.name ?? 'Unknown';
}

export function getStepTransitionMeta(
  meta: Record<string, unknown>,
  session?: { sortedSteps?: Array<{ id: string; name?: string }> } | null,
) {
  const fromStepId = (meta.from as string | null | undefined) ?? null;
  const toStepId = (meta.to as string | null | undefined) ?? null;

  return {
    from: {
      stepId: fromStepId,
      label: getHistoryStepLabel(fromStepId, session),
    },
    to: {
      stepId: toStepId,
      label: getHistoryStepLabel(toStepId, session),
    },
  };
}

export function fmtAuthor(email: string | null | undefined): string {
  if (!email) return '';
  return email;
}

function resolveModuleName(
  moduleId: string | undefined,
  session?: { data?: { modules?: Record<string, { name?: string }> } } | null,
): string | undefined {
  if (!moduleId) return undefined;
  return session?.data?.modules?.[moduleId]?.name;
}

export function describeGroupedConnectionEvent({
  isAdded,
  count,
  moduleId,
  authorEmail,
  session,
}: {
  isAdded: boolean;
  count: number;
  moduleId?: string;
  authorEmail?: string;
  session?: { data?: { modules?: Record<string, { name?: string }> } } | null;
}): string {
  const modName = resolveModuleName(moduleId, session);
  const author = fmtAuthor(authorEmail);
  const byAuthor = author ? ` by ${author}` : '';

  if (isAdded) {
    return modName ? `${count} added to ${modName}${byAuthor}` : `${count} added${byAuthor}`;
  }

  return modName ? `${count} removed from ${modName}${byAuthor}` : `${count} removed${byAuthor}`;
}

export function describeCardEvent(
  event: CardEvent,
  resolveCard: (id: string) => { cardNumber?: number } | null,
  session?: {
    data?: { modules?: Record<string, { name?: string }> };
    sortedSteps?: Array<{ id: string; name?: string }>;
  } | null,
): string {
  const author = fmtAuthor(event.userEmail);
  const meta = event.meta ?? {};
  const byAuthor = author ? ` by ${author}` : '';

  const descriptions: Record<CardEventType, () => string> = {
    'card.created': () => {
      const via = meta.via as string | undefined;
      if (via === 'breakout' && meta.sourceCardId) {
        const src = resolveCard(meta.sourceCardId as string);
        const label = src ? `Card #${src.cardNumber}` : 'original';
        return `Created via breakout from ${label}${byAuthor}`;
      }
      if (via === 'duplication' && meta.sourceCardId) {
        const src = resolveCard(meta.sourceCardId as string);
        const label = src ? `Card #${src.cardNumber}` : 'original';
        return `Duplicated from ${label}${byAuthor}`;
      }
      return `Card created${byAuthor}`;
    },
    'card.deleted': () => `Card deleted${byAuthor}`,
    'card.completed': () => `Marked as done${byAuthor}`,
    'card.reopened': () => `Marked in progress${byAuthor}`,
    'card.restored': () => `Card restored${byAuthor}`,
    'card.movedToStep': () => {
      const transitionMeta = getStepTransitionMeta(meta, session);
      const toLabel = transitionMeta.to.label;
      const fromLabel = transitionMeta.from.label;
      return `Moved to ${toLabel} from ${fromLabel}${byAuthor}`;
    },
    'connection.added': () => {
      const modName = resolveModuleName(meta.moduleId as string | undefined, session);
      const rowIds = (meta.rowIds as string[]) ?? [];
      const n = rowIds.length;
      const prefix = n > 1 ? `${n} added` : 'Added';
      return modName ? `${prefix} to ${modName}${byAuthor}` : `${prefix}${byAuthor}`;
    },
    'connection.removed': () => {
      const modName = resolveModuleName(meta.moduleId as string | undefined, session);
      const rowIds = (meta.rowIds as string[]) ?? [];
      const n = rowIds.length;
      const prefix = n > 1 ? `${n} removed` : 'Removed';
      return modName ? `${prefix} from ${modName}${byAuthor}` : `${prefix}${byAuthor}`;
    },
    'issue.reported': () => {
      const msg = meta.message as string | undefined;
      return msg ? `Issue flagged: "${msg}"${byAuthor}` : `Issue flagged${byAuthor}`;
    },
    'issue.resolved': () => `Issue resolved${byAuthor}`,
    'priority.changed': () => {
      const to = meta.to as string | null;
      const label = to ? to.charAt(0).toUpperCase() + to.slice(1) : 'None';
      return `Priority changed to ${label}${byAuthor}`;
    },
    'dates.changed': () => {
      const action = meta.action as string | undefined;
      const dateLabel = getDateLabelFromMeta(meta);
      const dateValue = meta.dateValue as string | undefined;
      if (action === 'removed') {
        return dateValue
          ? `${dateLabel} date removed (was ${dateValue})${byAuthor}`
          : `${dateLabel} date removed${byAuthor}`;
      }
      if (action === 'added') {
        return dateValue
          ? `${dateLabel} date added as ${dateValue}${byAuthor}`
          : `${dateLabel} date added${byAuthor}`;
      }
      if (dateValue) {
        return `${dateLabel} date updated to ${dateValue}${byAuthor}`;
      }
      return `${dateLabel} date updated${byAuthor}`;
    },
    'title.changed': () => {
      if (meta.cleared) return `Title cleared${byAuthor}`;
      const title = meta.title as string | undefined;
      return title ? `Title set to "${title}"${byAuthor}` : `Title changed${byAuthor}`;
    },
  };

  const fn = descriptions[event.type];
  return fn ? fn() : `${event.type}${byAuthor}`;
}
